import { Component, input, OnInit, signal, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { WorkflowStageRulesComponent } from "./workflow.stage.rules.component";

@TypeManagerDecorator("workflow-stage-rules-step")
@Component({
    selector: "workflow-stage-rules-step",
    template: `
        <div class="stage-rules-step-container">
            <div class="step-header">
                <h3>Stage Rules</h3>
                <p class="step-description">
                    Configure rules for each stage to control when they are enabled or disabled.
                </p>
            </div>

            <div class="stages-list">
                @for (stage of workflowProcess().stages; track stage.workflowStageId || stage.id) {
                    <div class="stage-item">
                        <div class="stage-header" (click)="toggleStageExpansion(stage)">
                            <div class="stage-info">
                                <span class="stage-name">{{ stage.stageName }}</span>
                                <span class="stage-flags">
                                    @if (stage.isStartStage) {
                                        <span class="flag start">Start</span>
                                    }
                                    @if (stage.isTerminal) {
                                        <span class="flag terminal">Terminal</span>
                                    }
                                </span>
                            </div>
                            <div class="stage-actions">
                                <span class="expand-icon" [class.expanded]="isStageExpanded(stage)">
                                    {{ isStageExpanded(stage) ? "▼" : "▶" }}
                                </span>
                            </div>
                        </div>

                        @if (isStageExpanded(stage)) {
                            <div class="stage-content">
                                @if (stage.workflowStageId && workflowProcess().workflowProcessId) {
                                    <workflow-stage-rules
                                        [selectedStage]="stage"
                                        [workflowProcessId]="workflowProcess().workflowProcessId"
                                    ></workflow-stage-rules>
                                } @else {
                                    <div class="rules-unavailable">
                                        <p>Stage rules will be available after saving the workflow process.</p>
                                    </div>
                                }
                            </div>
                        }
                    </div>
                }
            </div>

            @if ((workflowProcess().stages || []).length === 0) {
                <div class="no-stages">
                    <p>No stages defined. Please add stages in the previous step.</p>
                </div>
            }
        </div>
    `,
    styles: `
        .stage-rules-step-container {
            padding: 20px;
        }

        .step-header {
            margin-bottom: 24px;
        }

        .step-header h3 {
            margin: 0 0 8px 0;
            color: #333;
        }

        .step-description {
            margin: 0;
            color: #666;
            font-size: 14px;
        }

        .stages-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .stage-item {
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            overflow: hidden;
        }

        .stage-header {
            padding: 16px;
            background: #f8f9fa;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: background 0.2s;
        }

        .stage-header:hover {
            background: #e9ecef;
        }

        .stage-info {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .stage-name {
            font-weight: 500;
            color: #333;
        }

        .stage-flags {
            display: flex;
            gap: 8px;
        }

        .flag {
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 500;
            text-transform: uppercase;
        }

        .flag.start {
            background: #d4edda;
            color: #155724;
        }

        .flag.terminal {
            background: #f8d7da;
            color: #721c24;
        }

        .expand-icon {
            color: #666;
            font-size: 12px;
            transition: transform 0.2s;
        }

        .expand-icon.expanded {
            transform: rotate(90deg);
        }

        .stage-content {
            padding: 20px;
            border-top: 1px solid #e0e0e0;
        }

        .rules-unavailable {
            text-align: center;
            padding: 40px 20px;
            color: #666;
            background: #f8f9fa;
            border-radius: 6px;
        }

        .no-stages {
            text-align: center;
            padding: 40px 20px;
            color: #666;
        }
    `,
    standalone: true,
    imports: [CommonModule, WorkflowStageRulesComponent],
})
export class WorkflowStageRulesStepComponent implements OnInit {
    workflowProcess = input.required<WorkflowTypes.WorkflowProcess>();

    private expandedStages = signal<Set<number | string>>(new Set());

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit() {}

    toggleStageExpansion(stage: WorkflowTypes.WorkflowStage) {
        const stageKey = stage.workflowStageId || stage.id!;
        const expanded = this.expandedStages();

        if (expanded.has(stageKey)) {
            expanded.delete(stageKey);
        } else {
            expanded.add(stageKey);
        }

        this.expandedStages.set(new Set(expanded));
    }

    isStageExpanded(stage: WorkflowTypes.WorkflowStage): boolean {
        const stageKey = stage.workflowStageId || stage.id!;
        return this.expandedStages().has(stageKey);
    }
}
